import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Calendar, ChevronRight, BookOpen } from 'lucide-react';
import Header from '../components/Header';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Basic SEO tag helper (no extra dep). Rewrites <title> and known meta tags.
function useSEO({ title, description, canonical, ogImage }) {
  useEffect(() => {
    if (title) document.title = title;
    const setMeta = (name, content, attr = 'name') => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', description);
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:type', 'article', 'property');
    if (ogImage) setMeta('og:image', ogImage, 'property');
    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }
  }, [title, description, canonical, ogImage]);
}

export default function Blogs() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const canonical = typeof window !== 'undefined' ? `${window.location.origin}/blogs` : '';

  useSEO({
    title: 'બ્લોગ (Blog) — Kagajbook Online | ગુજરાત દસ્તાવેજ સેવા',
    description: 'સરકારી દસ્તાવેજ સેવાઓ, પ્રમાણપત્રો, GST, PAN, આધાર અને વધુ પર ગુજરાતી ભાષામાં ઉપયોગી માર્ગદર્શન અને લેખો.',
    canonical,
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/blogs`);
        setPosts(Array.isArray(res.data) ? res.data : []);
      } catch (e) { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return ''; }
  };

  const imageUrl = (p) => p ? `${API_URL}/api/public/files/${p.featured_image_path}` : '';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="blogs-page">
      <Header />
      {/* Hero */}
      <section className="bg-[#172554] text-white py-14 md:py-20 border-b-4 border-[#F5B800]">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-3 text-[#F5B800] text-xs font-bold uppercase tracking-widest mb-3">
            <BookOpen className="w-4 h-4" /> Kagajbook Blog
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-white leading-tight text-gujarati">
            માર્ગદર્શન, ટિપ્સ &amp; સમાચાર
          </h1>
          <p className="mt-3 text-white/70 max-w-2xl text-gujarati">
            સરકારી દસ્તાવેજ સેવાઓ પર ઉપયોગી લેખો — સરળ ગુજરાતી ભાષામાં.
          </p>
        </div>
      </section>

      {/* Posts grid */}
      <section className="max-w-6xl mx-auto px-4 py-10">
        {loading ? (
          <div className="text-center text-[#64748B] py-16">Loading…</div>
        ) : posts.length === 0 ? (
          <div className="text-center text-[#64748B] py-16 text-gujarati" data-testid="blogs-empty">
            હાલમાં કોઈ લેખ ઉપલબ્ધ નથી. ટૂંક સમયમાં પાછા આવો.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blogs-grid">
            {posts.map(p => (
              <Link
                to={`/blogs/${p.slug}`}
                key={p.id}
                className="group bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-lg hover:-translate-y-0.5 transition-all"
                data-testid={`blog-card-${p.slug}`}
              >
                {p.featured_image_path ? (
                  <div className="aspect-[16/9] bg-gray-100 overflow-hidden">
                    <img src={imageUrl(p)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                  </div>
                ) : (
                  <div className="aspect-[16/9] bg-gradient-to-br from-[#172554] to-[#0f1a3a] flex items-center justify-center">
                    <BookOpen className="w-10 h-10 text-[#F5B800]/60" />
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-center gap-2 text-[11px] text-[#64748B] mb-2">
                    <Calendar className="w-3 h-3" /> {formatDate(p.created_at)}
                  </div>
                  <h2 className="text-lg font-extrabold text-[#172554] leading-snug line-clamp-2 text-gujarati group-hover:text-[#F5B800] transition-colors">
                    {p.title}
                  </h2>
                  {p.meta_description && (
                    <p className="mt-2 text-sm text-[#64748B] line-clamp-3 text-gujarati">{p.meta_description}</p>
                  )}
                  <div className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-[#172554] group-hover:text-[#F5B800]">
                    Read more <ChevronRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
